import { useEffect, useRef } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation, useNavigate } from "react-router-dom";
import api, { setAuth, clearAuth, API_BASE } from "@/lib/api";
import { Toaster } from "@/components/ui/sonner";
import LoginPage from "@/pages/LoginPage";
import Dashboard from "@/pages/Dashboard";
import SwipeReview from "@/pages/SwipeReview";
import CandidatesList from "@/pages/CandidatesList";
import CandidateDetail from "@/pages/CandidateDetail";
import RolesAdmin from "@/pages/RolesAdmin";
import UploadPage from "@/pages/UploadPage";
import TeamPage from "@/pages/TeamPage";
import ScoresDashboard from "@/pages/ScoresDashboard";
import SettingsPage from "@/pages/SettingsPage";
import { Sidebar } from "@/components/Sidebar";
import { ProtectedRoute } from "@/components/ProtectedRoute";

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH

export const AuthCallback = () => {
  const navigate = useNavigate();
  const hasProcessed = useRef(false);

  useEffect(() => {
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    const hash = window.location.hash;
    const sessionId = new URLSearchParams(hash.substring(1)).get("session_id");

    if (!sessionId) {
      navigate("/login");
      return;
    }

    const processAuth = async () => {
      try {
        const resp = await api.post("/auth/session", { session_id: sessionId });
        const userData = resp.data;
        const token = userData.session_token;
        // Store auth in localStorage
        setAuth(token, userData);
        navigate("/dashboard", { state: { user: userData } });
      } catch (err) {
        const msg = err.response?.data?.detail || "Authentication failed";
        navigate("/login", { state: { error: msg } });
      }
    };
    processAuth();
  }, [navigate]);

  return null;
};

const AppLayout = ({ children, user, onLogout }) => {
  return (
    <div className="flex h-screen overflow-hidden" data-testid="app-layout">
      <Sidebar user={user} onLogout={onLogout} />
      <main className="flex-1 overflow-y-auto bg-[#09090B] w-full">
        <div className="pt-14 lg:pt-0">
          {children}
        </div>
      </main>
    </div>
  );
};

function AppRouter() {
  const location = useLocation();
  const navigate = useNavigate();

  // Check for session_id in hash synchronously
  if (location.hash?.includes("session_id=")) {
    return <AuthCallback />;
  }

  const handleLogout = async () => {
    try {
      await api.post("/auth/logout");
    } catch (e) { /* ignore */ }
    clearAuth();
    navigate("/login");
  };

  return (
    <Routes>
      <Route path="/login" element={<LoginPage />} />
      <Route path="/dashboard" element={
        <ProtectedRoute>
          {(user) => (
            <AppLayout user={user} onLogout={handleLogout}>
              <Dashboard user={user} />
            </AppLayout>
          )}
        </ProtectedRoute>
      } />
      <Route path="/swipe" element={
        <ProtectedRoute>
          {(user) => (
            <AppLayout user={user} onLogout={handleLogout}>
              <SwipeReview user={user} />
            </AppLayout>
          )}
        </ProtectedRoute>
      } />
      <Route path="/candidates" element={
        <ProtectedRoute>
          {(user) => (
            <AppLayout user={user} onLogout={handleLogout}>
              <CandidatesList user={user} />
            </AppLayout>
          )}
        </ProtectedRoute>
      } />
      <Route path="/candidates/:id" element={
        <ProtectedRoute>
          {(user) => (
            <AppLayout user={user} onLogout={handleLogout}>
              <CandidateDetail user={user} />
            </AppLayout>
          )}
        </ProtectedRoute>
      } />
      <Route path="/roles" element={
        <ProtectedRoute>
          {(user) => (
            <AppLayout user={user} onLogout={handleLogout}>
              <RolesAdmin user={user} />
            </AppLayout>
          )}
        </ProtectedRoute>
      } />
      <Route path="/upload" element={
        <ProtectedRoute>
          {(user) => (
            <AppLayout user={user} onLogout={handleLogout}>
              <UploadPage user={user} />
            </AppLayout>
          )}
        </ProtectedRoute>
      } />
      <Route path="/team" element={
        <ProtectedRoute>
          {(user) => (
            <AppLayout user={user} onLogout={handleLogout}>
              <TeamPage user={user} />
            </AppLayout>
          )}
        </ProtectedRoute>
      } />
      <Route path="/scores" element={
        <ProtectedRoute>
          {(user) => (
            <AppLayout user={user} onLogout={handleLogout}>
              <ScoresDashboard user={user} />
            </AppLayout>
          )}
        </ProtectedRoute>
      } />
      <Route path="/settings" element={
        <ProtectedRoute>
          {(user) => (
            <AppLayout user={user} onLogout={handleLogout}>
              <SettingsPage user={user} />
            </AppLayout>
          )}
        </ProtectedRoute>
      } />
      <Route path="*" element={
        <ProtectedRoute>
          {(user) => (
            <AppLayout user={user} onLogout={handleLogout}>
              <Dashboard user={user} />
            </AppLayout>
          )}
        </ProtectedRoute>
      } />
    </Routes>
  );
}

function App() {
  return (
    <div className="App noise-overlay">
      <BrowserRouter>
        <AppRouter />
      </BrowserRouter>
      <Toaster position="bottom-right" richColors />
    </div>
  );
}

export default App;
